import _ from 'lodash';

import { DOC_FIELD_TYPES } from 'constants/documentFieldTypes';

import {
  WORKSHEET_DEFAULT_SECTION_KEY,
  WORKSHEET_FIELD_SETTING,
  WORKSHEET_FIELD_WIDTH,
  WORKSHEET_NO,
  WORKSHEET_SETTINGS_SEPARATOR,
  WORKSHEET_SHOW_IF_VALUES_SEPARATOR,
  WORKSHEET_YES,
} from './WorksheetFieldsLayout.constants';

const SETTING_PATTERN = /^\s*([A-Za-z]+)\s*:\s*(.+?)\s*$/;
const SHOW_IF_PATTERN = /^(.+?)\s*(!?=)\s*(.+)$/;
const COMPUTE_PATTERN = /^([A-Za-z]+)\s*\((.*)\)$/;

const parseShowIf = value => {
  const match = SHOW_IF_PATTERN.exec(value);

  if (!match) {
    return null;
  }

  const [, fieldName, operator, expected] = match;

  return {
    fieldName: fieldName.trim(),
    isNegated: operator === '!=',
    values: expected
      .split(WORKSHEET_SHOW_IF_VALUES_SEPARATOR)
      .map(item => item.trim().toLowerCase())
      .filter(Boolean),
  };
};

/**
 * Reads a worksheet field's layout settings from its description: `section: Residence`,
 * `showIf: Is there a mortgage? = Yes` (`!=` and `Yes|No` alternatives allowed),
 * `format: currency|phone`, `width: full|half`, `prefill: loan.totalLoanAmount` (a new
 * worksheet's default answer, see WORKSHEET_PREFILL_SOURCE), `compute: payment(Customer Rate, Term,
 * Note Amount)` (calculated live from other answers or calculated values, by name; see
 * WORKSHEET_COMPUTE_FUNCTION). Lines that are not settings make up the hint.
 *
 * @param {string|null} description
 * @returns {{ section: string|null, showIf: object|null, format: string|null, width: string|null, prefill: string|null, compute: { name: string, args: string[] }|null, hint: string|null }}
 */
// `payment(Customer Rate, Term, Note Amount)` → { name: 'payment', args: ['Customer Rate', ...] }.
const parseCompute = text => {
  const match = COMPUTE_PATTERN.exec(text.trim());

  return match
    ? {
        name: match[1].toLowerCase(),
        args: match[2]
          .split(',')
          .map(arg => arg.trim())
          .filter(Boolean),
      }
    : null;
};

export const parseWorksheetFieldSettings = description => {
  const settings = {
    section: null,
    showIf: null,
    format: null,
    width: null,
    prefill: null,
    compute: null,
    hint: null,
  };
  const hintLines = [];

  String(description ?? '')
    .split(WORKSHEET_SETTINGS_SEPARATOR)
    .map(line => line.trim())
    .filter(Boolean)
    .forEach(line => {
      const match = SETTING_PATTERN.exec(line);
      const key = match?.[1].toLowerCase();

      switch (key) {
        case WORKSHEET_FIELD_SETTING.SECTION:
          settings.section = match[2];
          break;
        case WORKSHEET_FIELD_SETTING.SHOW_IF:
          settings.showIf = parseShowIf(match[2]);
          break;
        case WORKSHEET_FIELD_SETTING.FORMAT:
          settings.format = match[2].toLowerCase();
          break;
        case WORKSHEET_FIELD_SETTING.WIDTH:
          settings.width = match[2].toLowerCase();
          break;
        case WORKSHEET_FIELD_SETTING.PREFILL:
          settings.prefill = match[2].toLowerCase();
          break;
        case WORKSHEET_FIELD_SETTING.COMPUTE:
          settings.compute = parseCompute(match[2]);
          break;
        default:
          hintLines.push(line);
      }
    });

  settings.hint = hintLines.join(' ') || null;

  return settings;
};

// The field's own hint from its description, followed by any hint the worksheet adds (e.g. a prefill's source).
export const getWorksheetFieldHint = ({ settings, extraHint }) =>
  _.compact([settings.hint, extraHint]).join(' ') || null;

/**
 * A field's current answer as text, the way a showIf condition names it: Yes / No for Yes/No
 * questions, the option name for dropdowns, the text otherwise; null when unanswered.
 */
export const getWorksheetAnswerText = ({ type, value }) => {
  if (_.isNil(value) || value === '') {
    return null;
  }

  if (type === DOC_FIELD_TYPES.BOOL) {
    return value ? WORKSHEET_YES : WORKSHEET_NO;
  }

  if (_.isArray(value)) {
    return value.map(({ name }) => name).join(WORKSHEET_SHOW_IF_VALUES_SEPARATOR) || null;
  }

  if (_.isObject(value)) {
    return value.name ?? null;
  }

  return String(value);
};

/**
 * Whether a worksheet field is shown: always, unless its showIf condition names another question
 * whose current answer does not match (an unanswered parent hides its follow-ups).
 */
export const isWorksheetFieldVisible = ({ showIf, fieldsByName, form }) => {
  if (!showIf) {
    return true;
  }

  const parent = fieldsByName[showIf.fieldName];

  if (!parent) {
    return true;
  }

  const answer = getWorksheetAnswerText({ type: parent.type, value: form?.[parent.id] });

  if (_.isNull(answer)) {
    return false;
  }

  const answers = answer.toLowerCase().split(WORKSHEET_SHOW_IF_VALUES_SEPARATOR);
  const isMatch = showIf.values.some(expected => answers.includes(expected));

  return showIf.isNegated ? !isMatch : isMatch;
};

// Yes/No questions and long text read better across the whole row; short answers sit two abreast.
const isFullWidthByDefault = ({ type }) => type === DOC_FIELD_TYPES.BOOL;

export const isWorksheetFieldFullWidth = ({ field, settings }) =>
  settings.width ? settings.width === WORKSHEET_FIELD_WIDTH.FULL : isFullWidthByDefault(field);

/**
 * Groups the worksheet's top-level fields into sections in form order: a field with a `section`
 * setting starts a new one; fields before the first section form an untitled one.
 *
 * @param {Array<object>} fields extraction field tree (top level)
 * @returns {Array<{ key: string, title: string|null, items: Array<{ field: object, settings: object }> }>}
 */
export const buildWorksheetSections = (fields = []) =>
  fields.reduce((sections, field) => {
    const settings = parseWorksheetFieldSettings(field.description);
    const item = { field, settings };

    if (settings.section || !sections.length) {
      sections.push({
        key: settings.section ? field.id : WORKSHEET_DEFAULT_SECTION_KEY,
        title: settings.section,
        items: [item],
      });

      return sections;
    }

    _.last(sections).items.push(item);

    return sections;
  }, []);
